import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { buildPhaseFilterOptionsFromTemplates } from '../../../lib/caseListPhaseUtils';
import { CASE_STATUS_FILTER_OPTIONS } from '../../../lib/caseStatusUtils';

const defaultFilters = {
  status: '',
  phase: '',
  urgency: '',
  hasIncidents: '',
  messages: '',
  conception: '',
  administrativeAgentId: '',
  search: '',
  dentistId: '',
};

const CaseFilters = ({
  filters: filtersProp,
  onFilterChange,
  resultsCount,
  unreadMessagesTotal = null,
  showDentistFilter = false,
  dentistOptions = [],
  /** Vue dentiste : filtre suivi conception (hub dentiste) */
  showConceptionFilter = false,
  /** Vue staff : filtre par agent administratif (révision initiale) */
  showAdministrativeAgentFilter = false,
  administrativeAgentOptions = [],
  /** Vue dentiste : masque le filtre Statut (info redondante / non souhaitée) */
  hideStatusFilter = false,
}) => {
  const [internalFilters, setInternalFilters] = useState(defaultFilters);
  const isControlled = filtersProp != null;
  const filters = isControlled ? { ...defaultFilters, ...filtersProp } : internalFilters;

  const [phaseOptions, setPhaseOptions] = useState([
    { value: '', label: 'Toutes les phases' }
  ]);

  // Référentiel workflow (phase_templates), pas les libellés hétérogènes des lignes cas
  useEffect(() => {
    const fetchPhases = async () => {
      try {
        const { data, error } = await supabase
          .from('phase_templates')
          .select('id, phase_name, phase_order, group')
          .eq('is_active', true)
          .order('group', { ascending: true })
          .order('phase_order', { ascending: true });

        if (error) throw error;

        setPhaseOptions(buildPhaseFilterOptionsFromTemplates(data || []));
      } catch (error) {
        console.error('Error fetching phase templates:', error);
        setPhaseOptions([{ value: '', label: 'Toutes les phases' }]);
      }
    };

    fetchPhases();
  }, []);

  const urgencyOptions = [
    { value: '', label: 'Toutes les urgences' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'express', label: 'Express' },
    { value: 'normal', label: 'Normal' },
    { value: 'urgent', label: 'Urgente (ancien code urgent)' },
    { value: 'high', label: 'Haute (ancienne)' },
    { value: 'medium', label: 'Moyenne (ancienne)' },
    { value: 'low', label: 'Basse (ancienne)' },
  ];

  const incidentOptions = [
    { value: '', label: 'Tous les cas' },
    { value: 'yes', label: 'Avec incidents' },
    { value: 'no', label: 'Sans incidents' }
  ];

  const messageOptions = [
    { value: '', label: 'Tous les cas' },
    { value: 'with', label: 'Avec messages' },
    { value: 'without', label: 'Sans messages' },
    { value: 'unread', label: 'Messages non lus' },
  ];

  const conceptionOptions = [
    { value: '', label: 'Toutes les conceptions' },
    { value: 'nonSoumise', label: 'Non soumises' },
    {
      value: 'aValider',
      label: 'À valider (révision dentiste)',
    },
    { value: 'expediee', label: 'Expédiées' },
  ];

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    if (!isControlled) setInternalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleReset = () => {
    onFilterChange({ ...defaultFilters });
    if (!isControlled) setInternalFilters({ ...defaultFilters });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Filter" size={20} />
          Filtres avancés
        </h3>
        <Button
          variant="ghost"
          size="sm"
          iconName="RotateCcw"
          iconPosition="left"
          onClick={handleReset}
        >
          Réinitialiser
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
        {!hideStatusFilter && (
          <Select
            label="Statut"
            description="Cycle de vie de la commande, distinct de la phase."
            options={CASE_STATUS_FILTER_OPTIONS}
            placeholder="Tous les statuts"
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
          />
        )}

        <Select
          label="Phase"
          options={phaseOptions}
          value={filters?.phase}
          onChange={(value) => handleFilterChange('phase', value)}
        />

        <Select
          label="Urgence"
          options={urgencyOptions}
          value={filters?.urgency}
          onChange={(value) => handleFilterChange('urgency', value)}
        />

        <Select
          label="Incidents"
          options={incidentOptions}
          value={filters?.hasIncidents}
          onChange={(value) => handleFilterChange('hasIncidents', value)}
        />

        <Select
          label="Messages"
          options={messageOptions}
          value={filters?.messages ?? ''}
          onChange={(value) => handleFilterChange('messages', value)}
        />

        {showConceptionFilter && (
          <Select
            label="Suivi dentiste"
            options={conceptionOptions}
            value={filters?.conception ?? ''}
            onChange={(value) => handleFilterChange('conception', value)}
          />
        )}

        {showAdministrativeAgentFilter && (
          <Select
            label="Agent administratif"
            options={administrativeAgentOptions}
            value={filters?.administrativeAgentId ?? ''}
            onChange={(value) => handleFilterChange('administrativeAgentId', value)}
          />
        )}

        {showDentistFilter && (
          <Select
            label="Dentiste client"
            options={dentistOptions}
            value={filters?.dentistId}
            onChange={(value) => handleFilterChange('dentistId', value)}
          />
        )}
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Rechercher par nom de patient, ID de cas..."
            value={filters?.search}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <Icon name="FileText" size={16} className="shrink-0" />
          <span className="font-medium">
            {resultsCount} résultat{resultsCount !== 1 ? 's' : ''}
            {unreadMessagesTotal != null && unreadMessagesTotal > 0 && (
              <span className="text-foreground font-semibold">
                {' '}
                · {unreadMessagesTotal} message{unreadMessagesTotal !== 1 ? 's' : ''}{' '}
                non lu{unreadMessagesTotal !== 1 ? 's' : ''}
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CaseFilters;